<template>
  <section>
    <header class="top-bar">
      <div class="legend">
        <div class="first-row">
          <div class="legend-item">
            <h3 class="legend-title">{{ balance | formatCurrencyRounded }}</h3>
            <label v-t class="balance">Balance</label>
          </div>
          <div class="mobile-timeframes">
            <context-menu :displayed="mobileDatesMenuDisplayed" position="bottom-left" @close="closeMobileDatesMenu">
              <button slot="anchor" type="button" class="outline small" @click="openMobileDatesMenu">
                {{ activeTimeframeLabel.mobileLabel }}
              </button>
              <ul slot="content">
                <li
                  v-for="timeframeLabel in timeframeLabels" :key="timeframeLabel.value"
                  @click="$emit('time-frame-changed', timeframeLabel.value), closeMobileDatesMenu()"
                >
                  <a href="#" :class="{ active: timeframe === timeframeLabel.value }">{{ timeframeLabel.mobileLabel }}</a>
                </li>
              </ul>
            </context-menu>
          </div>
        </div>
        <div class="second-row">
          <div class="legend-item">
            <h4 class="legend-title">{{ cashflows | formatCurrencyRounded }}</h4>
            <label class="contributions">Contributions</label>
          </div>
          <div class="legend-item">
            <h4 class="legend-title">{{ ror | formatPercentage }}</h4>
            <label v-t>Return</label>
          </div>
          <div class="legend-item">
            <h4 class="legend-title">{{ gains | formatCurrencyRounded }}</h4>
            <label v-t>Gains</label>
          </div>
        </div>
      </div>
      <div class="desktop-timeframes">
        <button
          v-for="timeframeLabel in timeframeLabels" :key="timeframeLabel.value"
          :class="{ active: timeframe === timeframeLabel.value }" class="outline small" type="button"
          @click="$emit('time-frame-changed', timeframeLabel.value)"
        >
          {{ timeframeLabel.desktopLabel }}
        </button>
      </div>
    </header>
    <!-- svg gradient def for chart, allows for injectible colour variables-->
    <svg style="width:0;height:0;position:absolute;" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="gradient" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stop-color="var(--color-stop-1)" />
          <stop offset="100%" stop-color="var(--color-stop-2)" />
        </linearGradient>
      </defs>
    </svg>

    <div class="ct-chart" />
  </section>
</template>

<script>
import Chartist from 'chartist';
import '@/lib/chartist-plugins/ct-point-labels';
import '@/lib/chartist-plugins/only-points';
import '@/lib/chartist-plugins/only-x-labels';

import filters, { formatCurrencyWithMantissa } from '@/lib/utils/filters';
import contextMenu from '@/components/layout/context_menu';

/* istanbul ignore next: chartist code */
const buildChart = (dates, contributions, balances) => {
  const chart = new Chartist.Line(
    '.ct-chart',
    {
      labels: dates,
      series: [contributions, balances],
    },
    {
      chartPadding: {
        top: 30, right: 8, bottom: 20, left: 8,
      },
      showArea: true,
      showPoint: false,
      fullWidth: true,
      axisX: { showGrid: false, labelOffset: { y: 20 }, showLabel: false },
      axisY: { showGrid: false, offset: 0, showLabel: false },
      plugins: [
        Chartist.plugins.wbOnlyPoints(
          data => data.type === 'line',
          (pathElements, pathElement) => pathElement.data.valueIndex === 0 || pathElement.data.valueIndex === pathElements.length - 1,
        ),
        Chartist.plugins.wbAccountCtPointLabels({
          labelInterpolationFnc: (value) => {
            if (value) {
              return formatCurrencyWithMantissa(value);
            }
            return formatCurrencyWithMantissa(0);
          },
        }),
        Chartist.plugins.wbOnlyXLabels(
          (length, index) => index === 0 || index === length - 1,
          {
            labelWidth: 100,
          },
        ),
      ],
    },
  );

  chart.on('created', (ctx) => {
    // Draws the line between the x axis and the graph
    ctx.svg.querySelector('svg > g:nth-child(2)').elem('line', {
      x1: ctx.chartRect.x1,
      x2: ctx.chartRect.x2,
      y1: ctx.chartRect.y1 + 15,
      y2: ctx.chartRect.y1 + 15,
      stroke: '#d0d0d9',
      'stroke-width': 1,
    });
  });

  return chart;
};

const _ = { buildChart };

export default {
  _,
  components: {
    contextMenu,
  },
  filters,
  props: {
    balance: {
      type: Number,
      default: 0,
    },
    cashflows: {
      type: Number,
      default: 0,
    },
    ror: {
      type: Number,
      default: 0,
    },
    gains: {
      type: Number,
      default: 0,
    },
    timeframe: {
      type: String,
      validator: value => ['all', '1M', '6M', '1Y', 'YTD'].indexOf(value) !== -1,
      default: 'all',
    },
    dates: {
      type: Array,
      default: Array,
    },
    contributions: {
      type: Array,
      default: Array,
    },
    balances: {
      type: Array,
      default: Array,
    },
  },

  data() {
    return {
      mobileDatesMenuDisplayed: false,
      timeframeLabels: [
        { value: 'all', mobileLabel: 'All time', desktopLabel: 'All time' },
        { value: '1M', mobileLabel: '1 Month', desktopLabel: '1M' },
        { value: '6M', mobileLabel: '6 Months', desktopLabel: '6M' },
        { value: '1Y', mobileLabel: '1 Year', desktopLabel: '1Y' },
        { value: 'YTD', mobileLabel: 'Year To Date', desktopLabel: 'YTD' },
      ],
    };
  },

  computed: {
    activeTimeframeLabel() {
      return this.timeframeLabels.find(t => t.value === this.timeframe);
    },
  },

  watch: {
    dates() {
      this.chart = _.buildChart(this.dates, this.contributions, this.balances);
    },
    contributions() {
      this.chart = _.buildChart(this.dates, this.contributions, this.balances);
    },
    balances() {
      this.chart = _.buildChart(this.dates, this.contributions, this.balances);
    },
  },
  mounted() {
    this.chart = _.buildChart(this.dates, this.contributions, this.balances);
  },
  beforeDestroy() {
    this.chart.detach();
  },
  methods: {
    closeMobileDatesMenu() {
      this.mobileDatesMenuDisplayed = false;
    },
    openMobileDatesMenu() {
      this.mobileDatesMenuDisplayed = true;
    },
  },
};
</script>

<style lang="scss">
  .top-bar {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 1rem 0 0;
  }

  .legend {
    width: 100%;
    display: flex;
    flex-direction: column;

    @media #{$screen-width-medium} {
      flex-direction: row;
      width: auto;
    }

    .first-row,
    .second-row {
      display: flex;
      flex-direction: row;
    }

    .first-row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      margin-bottom: 1rem;

      @media #{$screen-width-medium} {
        margin-bottom: 0;
      }
    }

    .second-row {
      .legend-item:last-child {
        margin-right: 0;
      }
    }

    .legend-item {
      margin-right: 3rem;

      .legend-title {
        line-height: 1.375rem;
        margin: 0 0 0.3rem;
      }

      > label {
        display: block;
        margin-top: 0.2rem;
        font-weight: normal;

        &.balance::before,
        &.contributions::before {
          display: inline-block;
          height: 0.55rem;
          width: 0.55rem;
          margin-right: 0.3rem;
          content: "";
          border-radius: 2rem;
        }

        &.balance::before {
          background-color: $primary-300;
        }

        &.contributions::before {
          background-color: $secondary-300;
        }
      }
    }
  }

  @media #{$screen-width-medium} {
    .mobile-timeframes {
      display: none;
    }
  }

  .desktop-timeframes {
    display: none;
    margin-right: 0.4rem;

    @media #{$screen-width-medium} {
      display: block;
    }

    button {
      margin-left: 0.5rem;
    }
  }

  // set colour stops for gradiant based on theme colours
  #gradient {
    --color-stop-1: #{$primary-300};
    --color-stop-2: #{$white};
  }

  .ct-chart {
    width: 100%;
    position: relative;

    &::before {
      display: block;
      float: left;
      content: "";
      width: 0;
      height: 0;
      padding-bottom: 75%;

      @media #{$screen-width-small} {
        padding-bottom: 50%;
      }

      @media #{$screen-width-medium} {
        padding-bottom: 33.3333333333%;
      }
    }

    &::after {
      content: "";
      display: table;
      clear: both;
    }

    > svg {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
    }

    .ct-chart-line {
      overflow: visible;
    }

    g {
      .ct-line {
        stroke-width: 1px;
      }

      .ct-series {
        .ct-background-label {
          stroke: $pearl-50;
          stroke-width: 0.3rem;
        }

        text:nth-of-type(-n+3) {
          text-anchor: start !important;
        }

        text:nth-of-type(n+3) {
          text-anchor: end !important;
        }
      }

      .ct-series-a {
        .ct-area {
          fill: none;
        }

        .ct-background-label,
        .ct-label {
          display: none !important;
        }

        .ct-line,
        .ct-point {
          stroke: $secondary-300;
        }
      }

      .ct-series-b {
        .ct-area {
          fill: url(#gradient);

          /*
            Ridiculous fix for a ridiculous browser: Safari
            It does not support fill with a gradient for the path element, because why not?
            So I use the default jade colour
          */
          @supports (-webkit-marquee-repetition:infinite) and (object-fit:fill) {
            fill: $primary-300;
          }
        }

        .ct-line,
        .ct-point {
          stroke: $primary-300;
        }

        .ct-label {
          fill: $primary-500;
        }
      }
    }

    /* Puts the right label of the X axis to the right of the graph */
    /* stylelint-disable selector-type-case, selector-type-no-unknown */
    g.ct-labels foreignObject:last-child .ct-label {
      justify-content: flex-end;
    }
    /* stylelint-enable */

    .ct-label {
      color: $pearl-700;
      fill: rgba(0, 0, 0, 0.6);
      font-size: 0.9rem;
    }
  }

</style>
